import * as L from 'leaflet'
import { Logger, Presenter, type IPresenterOwner } from 'wdc-cube'
import type FilterManager from '../the-consumer-market/tcm_filter_manager'
import LMapClickSimulation from '../the-consumer-market/tcm_presenter_map/lmap_click_simulation'
import type BaseSelectionModeBehaviour from '../the-consumer-market/tcm_presenter_map/selection_mode_behaviour'
import { type SelectionModeOwner } from '../the-consumer-market/tcm_presenter_map/selection_mode_behaviour'
import AreaSelectionModeBehaviour from '../the-consumer-market/tcm_presenter_map/selection_mode_behaviour_area'
import CircleSelectionModeBehaviour from '../the-consumer-market/tcm_presenter_map/selection_mode_behaviour_circle'
import RouteSelectionModeBehaviour from '../the-consumer-market/tcm_presenter_map/selection_mode_behaviour_route'
import { MapCardScope } from '../the-consumer-market/tcm_scopes'

const LOG = Logger.get('MV-Presenter-Map')

const CIRCULO_INICIAL_KM = 10

export type MapHost = IPresenterOwner & {
    fetchData(): Promise<void>
}

export default class MvMapPresenter extends Presenter<MapCardScope, MapHost> implements SelectionModeOwner {
    constructor(owner: MapHost, scope: MapCardScope, filterManager: FilterManager) {
        super(owner, scope)
        this.filterManager = filterManager
        this.__behaviours = [this.__area, this.__route, this.__circle]
        for (const behaviour of this.__behaviours) behaviour.owner = this
    }

    // :: Instance

    readonly filterManager: FilterManager
    readonly keyboard = { ctrlKey: false }

    private readonly __area = new AreaSelectionModeBehaviour()
    private readonly __route = new RouteSelectionModeBehaviour()
    private readonly __circle = new CircleSelectionModeBehaviour()
    private readonly __behaviours: BaseSelectionModeBehaviour[]
    private __ativo?: BaseSelectionModeBehaviour
    private __map: L.Map | null = null
    private __ultimoClique?: L.LatLng
    private readonly __clickSimulation = new LMapClickSimulation()
    private readonly __onZoomEnd = () => this.__map && this.__ativo?.setZoom(this.__map.getZoom())

    // :: Lifecycle

    initialize() {
        const s = this.scope
        this.__clickSimulation.clickListener = this.__handleMapClick.bind(this)
        s.slider.update = this.update
        s.mode = 'map'
        s.slider.visible = false
        s.onMapChanged = this.action(this.__handleMapChanged)
        s.onSetRouteMode = this.action(this.__handleSetRouteMode)
        s.onSetAreaMode = this.action(this.__handleSetAreaMode)
        s.onSetCircleMode = this.action(this.__handleSetCircleMode)
        s.onSetMapMode = this.action(this.__handleSetNavegarMode)
        s.onClearFilters = this.action(this.__handleClearFilters)
        s.slider.onValueChanged = this.action(this.__handleSliderValueChanged)
    }

    override release() {
        this.__clickSimulation.unbind()
        this.__map?.off('zoomend', this.__onZoomEnd)
        super.release()
    }

    override onBeforeScopeUpdate() {
        this.scope.showClearButton = this.hasFilter()
        this.__ativo?.onBeforeScopeUpdate()
    }

    // :: API

    hasFilter() {
        return this.__behaviours.some((b) => b.hasFilter())
    }

    clear() {
        this.__ultimoClique = undefined
        for (const behaviour of this.__behaviours) behaviour.clear()
        this.update()
    }

    plotFromFilterManager() {
        this.__ultimoClique = undefined
        if (this.__map) {
            this.__area.plotFromFilter(this.__map, this.filterManager)
            this.__route.plotFromFilter(this.__map, this.filterManager)
            this.__circle.plotFromFilter(this.__map, this.filterManager)
        }
        this.update()
    }

    getMapLayerByUF(): L.Polygon | L.Polyline | undefined {
        return undefined
    }

    // :: Handlers

    private async __handleMapChanged(map: L.Map | null) {
        if (this.__map === map) return
        if (this.__map) {
            this.__clickSimulation.unbind()
            this.__map.off('zoomend', this.__onZoomEnd)
            for (const behaviour of this.__behaviours) behaviour.removeAllLayers()
        }
        this.__map = map
        if (map) {
            this.__clickSimulation.bind(map)
            map.on('zoomend', this.__onZoomEnd)
            for (const behaviour of this.__behaviours) behaviour.draw(map)
        }
    }

    private async __handleSetRouteMode() {
        this.__ativar(this.__route)
    }

    private async __handleSetAreaMode() {
        this.__ativar(this.__area)
    }

    private async __handleSetCircleMode() {
        this.__ativar(this.__circle)
    }

    private async __handleSetNavegarMode() {
        this.__ativar(undefined)
    }

    private async __handleClearFilters() {
        this.clear()
        await this.__aplicar()
    }

    private async __handleSliderValueChanged(value: number) {
        this.scope.slider.value = value
        if (this.__ativo?.hasFilter()) {
            await this.__aplicar()
        }
    }

    // :: Internals

    private __ativar(behaviour: BaseSelectionModeBehaviour | undefined) {
        const s = this.scope
        if (!behaviour || behaviour === this.__ativo) {
            this.__ativo = undefined
            s.mode = 'map'
            s.slider.visible = false
            return
        }
        this.__ativo = behaviour
        behaviour.setMode(this.__map ? this.__map.getZoom() : 4)
        if (behaviour === this.__circle && !behaviour.hasFilter() && s.slider.unit === 'km') {
            s.slider.value = Math.max(s.slider.value, CIRCULO_INICIAL_KM)
        }
        behaviour.onBeforeScopeUpdate()
    }

    private __handleMapClick(evt: L.LeafletMouseEvent) {
        const ativo = this.__ativo
        if (!ativo) return
        const ultimo = this.__ultimoClique
        this.__ultimoClique = evt.latlng
        if (ultimo && ultimo.equals(evt.latlng)) return
        this.keyboard.ctrlKey = !!(evt.originalEvent?.ctrlKey || evt.originalEvent?.metaKey)
        ativo.addLocation({ lat: evt.latlng.lat, lon: evt.latlng.lng })
        this.__aplicar().catch(LOG.caught)
    }

    private async __aplicar() {
        this.update()
        this.__ativo?.preparePoints()
        if (this.__map && this.__ativo) this.__ativo.draw(this.__map)
        for (const behaviour of this.__behaviours) behaviour.applyFilter(this.filterManager)
        await this.owner.fetchData()
    }
}
